import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../../redux/managePackageSlice';
import './ManageCampaign.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { PERMISSION_VALUES } from '../../utils/constant';

function ManageCampaignDetail({ campaign, onSave, onCancel, permission, isCreateMode }) {
  const dispatch = useDispatch();

  const { products } = useSelector(state => ({
    products: state.managePackage.products.resdata || []
  }));

  const [formData, setFormData] = useState({
    campaignName: '',
    campaignDescription: '',
    promoType: '',
    quantity: '',
    priceOff: '',
    promoCode: '',
    rateOff: '',
    remarks: '',
    startDate: null,
    endDate: null,
    selectedProducts: []
  });

  const [errors, setErrors] = useState({
    dateError: '',
    startDateError: ''
  });

  const [fieldDisabledStates, setFieldDisabledStates] = useState({
    promoType: false,
    quantity: false,
    priceOff: false,
    rateOff: false,
    promoCode: false,
    startDate: false,
    endDate: false,
    products: false
  });

  const [isCampaignEnded, setIsCampaignEnded] = useState(false);

  const isEditMode = !!campaign;

  const isSaveEnabled = () => {
    if (permission === PERMISSION_VALUES.FULL_ACCESS) {
      return !(isEditMode && isCampaignEnded);
    }
    else if (permission === PERMISSION_VALUES.WRITE) {
      return !isCreateMode && !(isEditMode && isCampaignEnded);
    }
    else if (permission === PERMISSION_VALUES.READ) {
      return false;
    }
    return false;
  };

  const isBackEnabled = [
    PERMISSION_VALUES.FULL_ACCESS,
    PERMISSION_VALUES.WRITE,
    PERMISSION_VALUES.READ
  ].includes(permission);

  useEffect(() => {
    dispatch(fetchProducts({ language: 'EN', region: 'SG' }));

    if (campaign) {
      const parseUTCDate = (utcDateString) => {
        if (!utcDateString) return null;
        const utcDate = new Date(utcDateString);
        const localDate = new Date(utcDate.getTime() - (8 * 60 * 60 * 1000));

        return localDate;
      };

      setFormData({
        campaignName: campaign.campaignName || '',
        campaignDescription: campaign.campaignDesc || '',
        promoType: campaign.promoType === 1 ? 'General promo' : 'Unique promo',
        quantity: campaign.maxCount || '',
        priceOff: campaign.discountAmount || '',
        promoCode: campaign.promoCode || '',
        rateOff: campaign.discountRate ? (campaign.discountRate * 100) : '',
        remarks: campaign.remarks || '',
        startDate: campaign.startDate ? parseUTCDate(campaign.startDate) : null,
        endDate: campaign.endDate ? parseUTCDate(campaign.endDate) : null,
        selectedProducts: campaign.productUuids || []
      });
    } else {
      setFormData({
        campaignName: '',
        campaignDescription: '',
        promoType: '',
        quantity: '',
        priceOff: '',
        promoCode: '',
        rateOff: '',
        remarks: '',
        startDate: null,
        endDate: null,
        selectedProducts: []
      });
    }
  }, [campaign, dispatch]);

  useEffect(() => {
    if (isEditMode && campaign) {
      const now = new Date();

      const parseUTCDate = (utcDateString) => {
        if (!utcDateString) return null;
        const utcDate = new Date(utcDateString);
        return new Date(utcDate.getTime() - (8 * 60 * 60 * 1000));
      };

      const savedStartDate = campaign.startDate ? parseUTCDate(campaign.startDate) : null;
      const savedEndDate = campaign.endDate ? parseUTCDate(campaign.endDate) : null;

      let newDisabledStates = {
        promoType: false,
        quantity: false,
        priceOff: false,
        rateOff: false,
        promoCode: false,
        startDate: false,
        endDate: false,
        products: false
      };

      let campaignEnded = false;

      if (savedEndDate && savedEndDate < now) {
        campaignEnded = true;
        newDisabledStates = {
          promoType: true,
          quantity: true,
          priceOff: true,
          rateOff: true,
          promoCode: true,
          startDate: true,
          endDate: true,
          products: true
        };
      }
      else if (savedStartDate && savedStartDate < now) {
        campaignEnded = false;
        newDisabledStates = {
          promoType: true,
          quantity: true,
          priceOff: true,
          rateOff: true,
          promoCode: true,
          startDate: true,
          endDate: false,
          products: true
        };
      }

      setIsCampaignEnded(campaignEnded);
      setFieldDisabledStates(newDisabledStates);
    } else {
      setIsCampaignEnded(false);
      setFieldDisabledStates({
        promoType: false,
        quantity: false,
        priceOff: false,
        rateOff: false,
        promoCode: false,
        startDate: false,
        endDate: false,
        products: false
      });
    }
  }, [isEditMode, campaign]);

  const getProductDisplayName = (product) => {
    return product.contentJson?.basicInfo?.name;
  };

  const validateDates = () => {
    const now = new Date();
    setErrors(prev => ({ ...prev, dateError: '', startDateError: '' }));

    let isValid = true;

    const parseUTCDate = (utcDateString) => {
      if (!utcDateString) return null;
      const utcDate = new Date(utcDateString);
      return new Date(utcDate.getTime() - (8 * 60 * 60 * 1000));
    };

    if (!isEditMode && formData.startDate && formData.startDate < now) {
      setErrors(prev => ({ ...prev, startDateError: 'Start date cannot be earlier than current time.' }));
      isValid = false;
    }

    if (isEditMode && campaign) {
      const savedStartDate = campaign.startDate ? parseUTCDate(campaign.startDate) : null;
      const isCampaignNotStarted = !savedStartDate || savedStartDate > now;

      if (isCampaignNotStarted && formData.startDate && formData.startDate < now) {
        setErrors(prev => ({ ...prev, startDateError: 'Start date cannot be earlier than current time.' }));
        isValid = false;
      }
    }

    if (formData.startDate && formData.endDate && formData.endDate <= formData.startDate) {
      setErrors(prev => ({ ...prev, dateError: 'The end date must be later than the start date.' }));
      isValid = false;
    }

    return isValid;
  };

  useEffect(() => {
    validateDates();
  }, [formData.startDate, formData.endDate, isCampaignEnded, isEditMode]);

  const handleInputChange = (e) => {
    if (isEditMode && isCampaignEnded) return;

    const { name, value } = e.target;

    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };

      if (!isEditMode && name === 'promoType' && value === 'Unique promo') {
        newData.promoCode = '';
      }

      // Ensure only one of priceOff or rateOff is filled
      if (name === 'priceOff' && value) {
        newData.rateOff = '';
      } else if (name === 'rateOff' && value) {
        newData.priceOff = '';
      }

      return newData;
    });
  };

  const handleProductSelect = (productId) => {
    if ((isEditMode && isCampaignEnded) || (isEditMode && fieldDisabledStates.products)) return;

    setFormData(prev => {
      const product = products.find(p => p.uuid === productId);
      if (!product) return prev;

      const isSelected = prev.selectedProducts.includes(product.uuid);
      return {
        ...prev,
        selectedProducts: isSelected
          ? prev.selectedProducts.filter(p => p !== product.uuid)
          : [...prev.selectedProducts, product.uuid]
      };
    });
  };

  const handleDateChange = (date, name) => {
    if ((isEditMode && isCampaignEnded) ||
      (isEditMode && name === 'startDate' && fieldDisabledStates.startDate) ||
      (isEditMode && name === 'endDate' && fieldDisabledStates.endDate)) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateDates()) {
      return;
    }

    onSave(formData);
  };

  const shouldDisablePromoCode = () => {
    if (isEditMode && isCampaignEnded) return true;

    if (isEditMode && formData.promoType === 'Unique promo') {
      return true;
    }
    if (!isEditMode && formData.promoType === 'Unique promo') {
      return true;
    }
    if (isEditMode && fieldDisabledStates.promoCode) {
      return true;
    }
    return false;
  };

  const getPromoCodeValue = () => {
    if (isEditMode && formData.promoType === 'Unique promo') {
      return campaign.promoCode || '';
    }

    return formData.promoCode;
  };

  const shouldDisableQuantity = () => {
    if (isEditMode && isCampaignEnded) return true;

    if (formData.promoType === 'General promo') {
      if (isEditMode && fieldDisabledStates.quantity) {
        return true;
      }
      return false;
    } else {
      return formData.promoType === 'General promo' || (isEditMode && fieldDisabledStates.quantity);
    }
  };

  return (
    <div className="manage-campaign-detail-container">
      <form onSubmit={handleSubmit} className="manage-campaign-detail-form">
        <div className="manage-campaign-detail-content">
          {/* Campaign Name */}
          <div className="manage-campaign-detail-row">
            <label className="manage-campaign-detail-label">Campaign name<span style={{ color: 'red' }}>*</span>:</label>
            <div className="manage-campaign-detail-input-container">
              <input
                type="text"
                name="campaignName"
                value={formData.campaignName}
                onChange={handleInputChange}
                className="manage-campaign-detail-input"
                placeholder="Enter campaign name"
                disabled={isEditMode && isCampaignEnded}
              />
            </div>
          </div>

          {/* Campaign Description */}
          <div className="manage-campaign-detail-row">
            <label className="manage-campaign-detail-label">Campaign description:</label>
            <div className="manage-campaign-detail-input-container">
              <input
                type="text"
                name="campaignDescription"
                value={formData.campaignDescription}
                onChange={handleInputChange}
                className="manage-campaign-detail-input"
                placeholder="Enter campaign description"
                disabled={isEditMode && isCampaignEnded}
              />
            </div>
          </div>

          {/* Promo Type */}
          <div className="manage-campaign-detail-row">
            <label className="manage-campaign-detail-label">Promo type<span style={{ color: 'red' }}>*</span>:</label>
            <div className="manage-campaign-detail-checkbox-group">
              <label className="manage-campaign-detail-checkbox-label">
                <input
                  type="radio"
                  name="promoType"
                  value="Unique promo"
                  checked={formData.promoType === 'Unique promo'}
                  onChange={handleInputChange}
                  className="manage-campaign-detail-checkbox"
                  disabled={isEditMode || (isEditMode && fieldDisabledStates.promoType)}
                />
                Unique promo
              </label>
              <label className="manage-campaign-detail-checkbox-label">
                <input
                  type="radio"
                  name="promoType"
                  value="General promo"
                  checked={formData.promoType === 'General promo'}
                  onChange={handleInputChange}
                  className="manage-campaign-detail-checkbox"
                  disabled={isEditMode || (isEditMode && fieldDisabledStates.promoType)}
                />
                General promo
              </label>
            </div>
          </div>

          {/* Quantity and Price Off */}
          <div className="manage-campaign-detail-row">
            <label className="manage-campaign-detail-label">Quantity<span style={{ color: 'red' }}>*</span></label>
            <div className="manage-campaign-detail-input-group">
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                className="manage-campaign-detail-input small"
                placeholder="Qty"
                disabled={shouldDisableQuantity()}
              />
              <span className="manage-campaign-detail-input-text">Price off ($):</span>
              <input
                type="number"
                name="priceOff"
                value={!formData.rateOff ? formData.priceOff : ''}
                onChange={handleInputChange}
                className="manage-campaign-detail-input small"
                placeholder="0.00"
                disabled={!!formData.rateOff || (isEditMode && fieldDisabledStates.priceOff)}
                step="0.01"
                min="0"
              />
            </div>
          </div>

          {/* Promo Code and Rate Off */}
          <div className="manage-campaign-detail-row">
            <label className="manage-campaign-detail-label">Promo code<span style={{ color: 'red' }}>*</span>:</label>
            <div className="manage-campaign-detail-input-group">
              <input
                type="text"
                name="promoCode"
                value={getPromoCodeValue()}
                onChange={handleInputChange}
                className="manage-campaign-detail-input small"
                placeholder="Enter promo code"
                disabled={shouldDisablePromoCode()}
              />
              <span className="manage-campaign-detail-input-text">Rate off (%):</span>
              <input
                type="number"
                name="rateOff"
                value={!formData.priceOff ? formData.rateOff : ''}
                onChange={handleInputChange}
                className="manage-campaign-detail-input small"
                disabled={!!formData.priceOff || (isEditMode && fieldDisabledStates.rateOff)}
                placeholder="%"
                min="1"
                max="100"
              />
            </div>
          </div>

          <div className="manage-campaign-detail-row">
            <label className="manage-campaign-detail-label">Remarks:</label>
            <div className="manage-campaign-detail-input-container">
              <input
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                className="manage-campaign-detail-input"
                placeholder="Enter remarks"
                rows="3"
                disabled={isEditMode && isCampaignEnded}
              />
            </div>
          </div>

          {/* Start Date & Time */}
          <div className="manage-campaign-detail-row">
            <label className="manage-campaign-detail-label">Start date & time<span style={{ color: 'red' }}>*</span>:</label>
            <div className="manage-campaign-detail-input-container">
              <div className='manage-campaign-detail-date-input'>
                <DatePicker
                  selected={formData.startDate}
                  onChange={(date) => handleDateChange(date, 'startDate')}
                  dateFormat="MM/dd/yyyy h:mm:ss aa"
                  timeInputLabel="Time:"
                  showTimeInput
                  className="manage-campaign-detail-input"
                  placeholderText="Select start date & time"
                  showIcon
                  shouldCloseOnSelect={false}
                  disabled={fieldDisabledStates.startDate}
                  minDate={
                    isEditMode && campaign
                      ? (() => {
                        const savedStartDate = campaign.startDate ? new Date(campaign.startDate) : null;
                        const now = new Date();
                        if (!savedStartDate || savedStartDate > now) {
                          return new Date();
                        }
                        return savedStartDate;
                      })()
                      : new Date()
                  }
                />
              </div>
              {errors.startDateError && (
                <span className="error-message" style={{ color: 'red' }}>
                  {errors.startDateError}
                </span>
              )}
            </div>
          </div>

          {/* End Date & Time */}
          <div className="manage-campaign-detail-row">
            <label className="manage-campaign-detail-label">End date & time<span style={{ color: 'red' }}>*</span>:</label>
            <div className="manage-campaign-detail-input-container">
              <div className='manage-campaign-detail-date-input'>
                <DatePicker
                  selected={formData.endDate}
                  onChange={(date) => handleDateChange(date, 'endDate')}
                  dateFormat="MM/dd/yyyy h:mm:ss aa"
                  className="manage-campaign-detail-input"
                  placeholderText="Select end date & time"
                  showIcon
                  timeInputLabel="Time:"
                  showTimeInput
                  minDate={formData.startDate}
                  shouldCloseOnSelect={false}
                  disabled={isEditMode && fieldDisabledStates.endDate}
                />
              </div>

              {errors.dateError && (
                <span className="error-message" style={{ color: 'red' }}>
                  {errors.dateError}
                </span>
              )}
            </div>
          </div>

          {/* Product Selection */}
          <div className="manage-campaign-detail-row">
            <label className="manage-campaign-detail-label">Product<span style={{ color: 'red' }}>*</span></label>
            <div className="manage-campaign-detail-product-container">
              <div className="manage-campaign-detail-product-list">
                {products.map((product) => (
                  <label key={product.uuid} className="manage-campaign-detail-product-item">
                    <input
                      type="checkbox"
                      checked={formData.selectedProducts.includes(product.uuid)}
                      onChange={() => handleProductSelect(product.uuid)}
                      className="manage-campaign-detail-product-checkbox"
                      disabled={isEditMode && fieldDisabledStates.products}
                    />
                    <span className="manage-campaign-detail-product-text">
                      {getProductDisplayName(product)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="manage-campaign-detail-footer">
          <button
            type="button"
            onClick={onCancel}
            className="manage-campaign-detail-save-btn"
            style={{
              opacity: isBackEnabled ? 1 : 0.5,
              cursor: isBackEnabled ? 'pointer' : 'default',
              pointerEvents: isBackEnabled ? 'auto' : 'none'
            }}
          >
            Back
          </button>
          <button
            type="submit"
            className="manage-campaign-detail-save-btn"
            disabled={!isSaveEnabled()}
            style={{
              opacity: isSaveEnabled() ? 1 : 0.5,
              cursor: isSaveEnabled() ? 'pointer' : 'default'
            }}
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

export default ManageCampaignDetail;